import { IItemDelta } from '@augloop/types-core';

/**
 * Describes the type of the delta being applied.
 */
export enum TextTileDeltaType {
    // Adds new content to the existing tile at the specified position
    Add,

    // Deletes content from the existing tile at the specified position
    Delete,

    // Updates the content of the existing tile at the specified position
    Update
}

/**
 * The unit on which this delta is operating.
 */
export enum TextTileDeltaUnit {
    // Character level changes of no semantic meaning on their own.
    Chars,

    // Adding/deleting/updating a single word.
    Word,

    // Adding/deleting/updating a part of a sentence.
    PartialSentence,

    // Adding/deleting/updating a full sentence.
    Sentence
}

/**
 * Specifies a range in the tile that will become invalid after the delta is applied.
 */
export interface IInvalidationRange {
    // Start position of the invalidation range.
    readonly position: number;

    // Length of the invalidation range.
    readonly length: number;

    // The unit of the range that will be invalidated.
    readonly unit?: TextTileDeltaUnit;

    // LanguageId for range (format: RFC 4646)
    readonly languageId?: string;
}

/**
 * Text tile deltas can be applied to existing text tiles to transform the current tile revision
 * to the new one. They also contain some semantic information about the delta if it happens to
 * complete a word or a sentence for workflows that require this information.
 */
export interface ITextTileDelta extends IItemDelta {
    // Whether this delta is an add, delete or update to the current tile.
    readonly deltaType: TextTileDeltaType;

    // Extra semantic information for workflows that can consume this delta on whether the updates
    // in the delta complete a word or a sentence.
    readonly unit: TextTileDeltaUnit;

    // The start position at which to apply this delta.
    readonly position: number;

    // The length of the delta, this is only used for delete and update deltas.
    readonly length?: number;

    // The content of the delta, this is used only for add/update deltas. Delete deltas need to send
    // position and length only.
    readonly content?: string;

    // Extra metadata on the current word count of the paragraph after applying this delta.
    // This can be used by workflows directly consuming the delta as well as a basic checksum.
    readonly wordCount?: number;

    // An array of ranges in the text tile that will become invalid after applying this delta.
    // These ranges allow workflows consuming the deltas directly to know which parts of the tile
    // need to be rechecked especially for workflows that produce range annotations where the change
    // in offsets requires the annotations to be recreated.
    readonly invalidationRanges?: IInvalidationRange[];
}

/**
 * Formatted text tile deltas work identically to text tile deltas but are also used to provide
 * delta information about formatted ranges.
 */
export interface IFormattedTextTileDelta extends ITextTileDelta {}
